/**
 * ContentEditorPage — dedicated full-page editor for a single article or
 * case study. Opens in its own tab (via a `<a target="_blank">` link from
 * the LandingPageEditor's Add/Edit/Draft-this actions).
 *
 * Routes handled:
 *   /doctor/content/article/new                     — blank new article
 *   /doctor/content/article/new?from_topic=xxx      — new article from a
 *                                                     suggested topic
 *                                                     (creates draft on
 *                                                     mount, then redirects
 *                                                     to /edit/{id})
 *   /doctor/content/article/:id/edit                — edit existing
 *   /doctor/content/case_study/new                  — blank new case study
 *   /doctor/content/case_study/:id/edit             — edit existing
 *
 * Layout: left column = form fields (kind-driven); right column = live
 * public preview mirroring PublicArticlePage / PublicCasePage. Sticky top
 * bar hosts the breadcrumb, status chip, and Save + Publish buttons.
 *
 * Behaviour:
 *  - Autosave: 2.5s after last edit, once the item has an id.
 *  - beforeunload guard when there are unsaved changes.
 *  - On every save, broadcast a `content-updated` event so the original
 *    LandingPageEditor tab refreshes its list silently.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    ArrowLeft, Camera, CaretRight, CheckCircle, Clock,
    FloppyDisk, ImageSquare, MagnifyingGlass, PaperPlaneTilt, Sparkle,
    Warning, X, Circle, CheckCircle as CheckCircleIcon, LinkSimple,
} from "@phosphor-icons/react";
import RelatedContentPicker from "@/components/doctor/RelatedContentPicker";


const VALID_KINDS = new Set(["article", "case_study"]);

const KIND_META = {
    article:    { label: "Article", plural: "Articles" },
    case_study: { label: "Case Study", plural: "Case Studies" },
};

/* Accent palette mirrored from PublicArticlePage so the preview looks like the real page. */
const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;


function defaultFor(kind) {
    if (kind === "article") {
        return {
            // iter100.171 — `published_at` free-text field removed from the
            // authoring flow. The real publish timestamp is now set when a
            // reviewer approves the article (server-side `reviewed_at`).
            title: "", summary: "", body: "", url: "",
            cover_asset_id: null, cover_image: null,
            // iter100.172 — Author-picked internal links (up to 3).
            related_ids: [],
        };
    }
    if (kind === "case_study") {
        return {
            title: "", treatment: "", duration: "", description: "",
            concern_type: null, complexity: null, aligner_count: null,
            patient_consent_given: true,
            before_asset_id: null, before_image: null,
            after_asset_id: null, after_image: null,
            // iter100.172 — Author-picked internal links (up to 3).
            related_ids: [],
        };
    }
    return {};
}

/* Preview-only fields aren't part of the persisted schema; strip them
   before every save so the backend validator doesn't reject them. */
function stripPreviewFields(d) {
    const out = { ...d };
    delete out.cover_image;
    delete out.before_image;
    delete out.after_image;
    return out;
}

async function compressImage(file, maxDim = 1400, quality = 0.82) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}


export default function ContentEditorPage() {
    const { kind, id: routeId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const isNew = !routeId || routeId === "new";
    const fromTopic = searchParams.get("from_topic") || null;

    const [state, setState] = useState({
        loading: true,
        contentId: isNew ? null : routeId,
        kind,
        data: defaultFor(kind),
        status: "draft",
        doctor: null,
        error: null,
    });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    // Guard against invalid kind (someone typed a bad URL). We keep this
    // AFTER every hook so we don't violate React's rules-of-hooks; the
    // effects below no-op when kind is invalid because they only run
    // after this render returns.
    const isInvalidKind = !VALID_KINDS.has(kind);

    /* ------------------------------------------------------------------
       Initial load. Three paths:
         1. from_topic present     → POST /from-topic → replace URL to /edit/{id}
         2. routeId present        → GET /doctor/content/{kind} + find by id
         3. neither                → blank new form
       Doctor sidebar is fetched separately for the live preview.
       ------------------------------------------------------------------ */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (isInvalidKind) return;
            try {
                // Fetch doctor identity for the preview (name, accent, slug, photo)
                const doctorPromise = api.get("/doctor/landing-page/edit");

                if (fromTopic && isNew) {
                    const { data: created } = await api.post(
                        `/doctor/content/from-topic/${fromTopic}`,
                        {},
                    );
                    if (cancelled) return;
                    const row = created?.row || {};
                    const doctorRes = await doctorPromise;
                    if (cancelled) return;
                    setState({
                        loading: false,
                        contentId: created.content_id,
                        kind,
                        data: { ...defaultFor(kind), ...(row.data || {}) },
                        status: row.status || "draft",
                        doctor: doctorRes.data || null,
                        error: null,
                    });
                    setLastSavedAt(Date.now());
                    // Replace the URL so a page refresh brings the doctor
                    // back to this same draft (not a new POST loop).
                    navigate(`/doctor/content/${kind}/${created.content_id}/edit`, { replace: true });
                    return;
                }

                if (!isNew) {
                    // Load existing item. There's no per-id GET yet; the
                    // list endpoint returns everything for this kind and
                    // we filter client-side. Cheap because the list is
                    // capped at 100.
                    const [listRes, doctorRes] = await Promise.all([
                        api.get(`/doctor/content/${kind}`),
                        doctorPromise,
                    ]);
                    if (cancelled) return;
                    const items = Array.isArray(listRes.data) ? listRes.data : [];
                    const row = items.find((it) => it.id === routeId);
                    if (!row) {
                        setState((s) => ({ ...s, loading: false, error: "not_found" }));
                        return;
                    }
                    setState({
                        loading: false,
                        contentId: row.id,
                        kind,
                        data: { ...defaultFor(kind), ...(row.data || {}) },
                        status: row.status || "published",
                        doctor: doctorRes.data || null,
                        error: null,
                    });
                    setLastSavedAt(Date.now());
                    return;
                }

                // Blank new form
                const doctorRes = await doctorPromise;
                if (cancelled) return;
                setState({
                    loading: false,
                    contentId: null,
                    kind,
                    data: defaultFor(kind),
                    status: "draft",
                    doctor: doctorRes.data || null,
                    error: null,
                });
            } catch (e) {
                if (cancelled) return;
                setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || "load_failed" }));
            }
        })();
        return () => { cancelled = true; };
    }, [kind, routeId, fromTopic, isInvalidKind]);

    /* ------------------------------------------------------------------
       Field patcher. Marks the form dirty so autosave + beforeunload
       kick in on the next tick.
       ------------------------------------------------------------------ */
    const patch = (k, v) => {
        setState((s) => ({ ...s, data: { ...s.data, [k]: v } }));
        setDirty(true);
    };

    /* ------------------------------------------------------------------
       Image upload — mirrors the AssetPicker flow used inside the inline
       ContentEditor. Uses the same /doctor/content/asset endpoint.
       ------------------------------------------------------------------ */
    const uploadImage = async (assetKey, file) => {
        if (!file || !file.type.startsWith("image/")) {
            toast.error("Please pick an image");
            return;
        }
        try {
            const data_url = await compressImage(file, 1400, 0.82);
            const { data: res } = await api.post("/doctor/content/asset", { data_url });
            const previewField = assetKey.replace(/_asset$/, "") + "_image";
            patch(`${assetKey}_id`, res.id);
            patch(previewField, res.data_url);
            toast.success("Image uploaded");
        } catch {
            toast.error("Upload failed");
        }
    };

    /* ------------------------------------------------------------------
       Save. Creates on first save (POST) or updates (PUT) thereafter.
       Broadcasts a content-updated event to any other tab that has the
       LandingPageEditor open, so their list refreshes silently.

       Held in a ref so the autosave `useEffect` below can call the
       latest version without needing to list every closure dependency
       (which would otherwise cause the effect to re-fire on every
       keystroke and spawn duplicate save timers).
       ------------------------------------------------------------------ */
    const saveRef = useRef();
    saveRef.current = async ({ silent = false } = {}) => {
        const s = stateRef.current;
        if (saving) return s.contentId || null;
        setSaving(true);
        try {
            const payload = { data: stripPreviewFields(s.data) };
            let currentId = s.contentId;
            if (currentId) {
                await api.put(`/doctor/content/item/${currentId}`, payload);
            } else {
                const { data: created } = await api.post(`/doctor/content/${kind}`, payload);
                currentId = created.id;
                setState((prev) => ({ ...prev, contentId: created.id, status: created.status || prev.status }));
                // Replace URL so refresh keeps editing this row
                navigate(`/doctor/content/${kind}/${created.id}/edit`, { replace: true });
            }
            setDirty(false);
            setLastSavedAt(Date.now());
            if (!silent) toast.success("Saved");
            return currentId;
        } catch (e) {
            const msg = e?.response?.data?.detail || "Save failed";
            if (!silent) toast.error(msg);
            return null;
        } finally {
            setSaving(false);
        }
    };
    const save = (opts) => saveRef.current(opts);

    /* ------------------------------------------------------------------
       iter100.161 — One-click "Optimise for SEO" (Path A).
       Sends the current item to Gemini 3 Flash server-side, which rewrites
       the summary (articles) or description (case studies) into a 120-160
       char meta-description-friendly sentence. The new copy is persisted
       server-side and reflected in the editor field. A toast offers an
       Undo (restoring the pre-AI value locally + saving it back on click).
       ------------------------------------------------------------------ */
    const [optimizingSeo, setOptimizingSeo] = useState(false);
    const optimizeSeo = async () => {
        if (!state.contentId) {
            toast.error("Save the draft first so the AI has something to optimise.");
            return;
        }
        const field = kind === "article" ? "summary" : "description";
        const original = ((state.data || {})[field] || "").trim();
        if (!state.data.title?.trim()) {
            toast.error("Add a title first — the AI uses it to anchor the description.");
            return;
        }
        setOptimizingSeo(true);
        try {
            const { data: res } = await api.post(`/doctor/content/item/${state.contentId}/optimize-seo`);
            patch(field, res.optimized);
            setDirty(false);   // server already persisted the write
            setLastSavedAt(Date.now());
            toast.success("Summary optimised for SEO", {
                description: res.optimized.length > 90 ? `${res.optimized.slice(0, 90)}…` : res.optimized,
                action: original ? {
                    label: "Undo",
                    onClick: async () => {
                        patch(field, original);
                        // Persist the revert immediately so we don't leave the
                        // AI copy stuck on the server if the doctor never saves.
                        try {
                            await api.put(`/doctor/content/item/${state.contentId}`, {
                                data: stripPreviewFields({ ...state.data, [field]: original }),
                            });
                            setDirty(false);
                            setLastSavedAt(Date.now());
                            toast.info("Reverted to your original summary");
                        } catch {
                            toast.error("Undo saved locally but couldn't reach the server — click Save.");
                        }
                    },
                } : undefined,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "SEO optimisation failed. Please try again.");
        } finally {
            setOptimizingSeo(false);
        }
    };

    /* ------------------------------------------------------------------
       Autosave — debounce 2500ms after last keystroke, only when the row
       already exists (i.e. we have a contentId). First save must be
       manual so the doctor doesn't accidentally publish an empty stub.
       ------------------------------------------------------------------ */
    useEffect(() => {
        if (!dirty || !state.contentId) return undefined;
        const t = setTimeout(() => { saveRef.current({ silent: true }); }, 2500);
        return () => clearTimeout(t);
    }, [dirty, state.data, state.contentId]);

    /* ------------------------------------------------------------------
       beforeunload guard when there are unsaved changes.
       ------------------------------------------------------------------ */
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    /* ------------------------------------------------------------------
       Publish — for articles (which auto-publish), simply save + broadcast.
       For case studies (which need admin review), call the submit-for-
       review endpoint.

       iter100.157y — Bug fix: previous flow used a recursive `setTimeout`
       to re-invoke publish() after the first save, which captured a
       stale closure of `state.contentId` and looped forever emitting
       "Saved" toasts. Now save() returns the created id and we branch
       on that value locally — one save, one publish, no loops.
       ------------------------------------------------------------------ */
    const publish = async () => {
        if (publishing || saving) return;
        setPublishing(true);
        try {
            // Ensure we have a content id — save first if this is a brand
            // new draft. `save()` returns the (possibly newly-created) id.
            let contentId = state.contentId;
            if (!contentId) {
                contentId = await save({ silent: true });
                if (!contentId) return;  // save failed; toast already shown
            }
            if (kind === "case_study") {
                await api.post(`/doctor/content/item/${contentId}/submit`);
                setState((s) => ({ ...s, status: "pending_review" }));
                toast.success("Sent for review — usually approved within 24 hours");
            } else {
                // Articles auto-publish. If contentId was already set at the
                // top, do a fresh silent save to persist any pending edits.
                if (state.contentId) {
                    await save({ silent: true });
                }
                toast.success("Article saved. It will appear on your public page shortly.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not publish");
        } finally {
            setPublishing(false);
        }
    };

    if (isInvalidKind) {
        return <NotFoundState message="This editor doesn't support that content type." />;
    }

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading editor…</div>
            </div>
        );
    }

    if (state.error === "not_found") {
        return <NotFoundState message={`This ${KIND_META[kind].label.toLowerCase()} doesn't exist or was deleted.`} />;
    }

    const accent = pickAccent(state.doctor?.accent);
    const returnHref = "/doctor/landing-page?tab=" + kind;

    return (
        <div className="min-h-screen bg-slate-50" data-testid="content-editor-page">
            <title>{`${isNewLabel(state)} · ${KIND_META[kind].label} · Invisique™`}</title>

            {/* ═══ TOP BAR (sticky) ═══ */}
            <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
                    <Link
                        to={returnHref}
                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[12px] font-semibold text-slate-600 hover:bg-slate-100"
                        data-testid="editor-back-link"
                    >
                        <ArrowLeft size={12} weight="bold" />
                        <span className="hidden sm:inline">Back to {KIND_META[kind].plural}</span>
                        <span className="sm:hidden">Back</span>
                    </Link>
                    <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-slate-500 truncate min-w-0 flex-1">
                        <CaretRight size={10} weight="bold" className="opacity-50 shrink-0" />
                        <span className="truncate">{isNewLabel(state)}</span>
                    </div>
                    <StatusChip status={state.status} />
                    <SaveStatus dirty={dirty} saving={saving} lastSavedAt={lastSavedAt} />
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={optimizeSeo}
                            disabled={optimizingSeo || !state.contentId || !state.data.title}
                            title={
                                !state.contentId
                                    ? "Save the draft first"
                                    : !state.data.title
                                        ? "Add a title first"
                                        : "Rewrite the summary with an AI-optimised meta description (120-160 chars)"
                            }
                            data-testid="editor-optimize-seo-btn"
                        >
                            <Sparkle size={13} weight={optimizingSeo ? "regular" : "fill"} className={optimizingSeo ? "animate-pulse" : ""} />
                            <span className="hidden sm:inline">{optimizingSeo ? "Optimising…" : "Optimise for SEO"}</span>
                            <span className="sm:hidden">{optimizingSeo ? "…" : "SEO"}</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => save()}
                            disabled={saving || (!dirty && !!state.contentId)}
                            data-testid="editor-save-btn"
                        >
                            <FloppyDisk size={13} /> Save
                        </Button>
                        <Button
                            size="sm"
                            onClick={publish}
                            disabled={publishing || !state.data.title}
                            className="font-semibold"
                            data-testid="editor-publish-btn"
                        >
                            <PaperPlaneTilt size={13} weight="fill" />
                            {kind === "case_study" ? "Submit for review" : "Publish"}
                        </Button>
                    </div>
                </div>
            </header>

            {/* ═══ TWO-COLUMN LAYOUT ═══ */}
            <div className="max-w-[1440px] mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LEFT: Form */}
                <div className="min-w-0" data-testid="editor-form-column">
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
                        {kind === "article" && <ArticleFields data={state.data} patch={patch} uploadImage={uploadImage} doctor={state.doctor} contentId={state.contentId} />}
                        {kind === "case_study" && <CaseStudyFields data={state.data} patch={patch} uploadImage={uploadImage} doctor={state.doctor} contentId={state.contentId} />}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-3 px-1">
                        Autosaves 2.5 seconds after your last edit. Use{" "}
                        <span className="font-semibold text-slate-700">Publish</span> when you&apos;re ready for patients to see it.
                    </p>
                </div>

                {/* RIGHT: Live preview */}
                <div className="min-w-0" data-testid="editor-preview-column">
                    <div className="lg:sticky lg:top-20">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                            <Sparkle size={11} weight="fill" />
                            Live preview
                        </div>
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            {kind === "article" && (
                                <ArticlePreview data={state.data} doctor={state.doctor} accent={accent} />
                            )}
                            {kind === "case_study" && (
                                <CaseStudyPreview data={state.data} doctor={state.doctor} accent={accent} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ==========================================================================
   Article field renderer
   ========================================================================== */
/* ==========================================================================
   iter100.162 — SerpPreviewMini
   ==========================================================================
   Compact "how it will look on Google" card, rendered under the Summary /
   Description field. Sibling of the full SerpPreview in creator/SeoPanel
   but stripped down (no desktop/mobile toggle, no favicon flourishes) so
   it stays visually quiet under the editor. Live-updates as the doctor
   types + as the "Optimise for SEO" button rewrites the summary.
   ========================================================================== */
function _slugifyForPreview(text) {
    if (!text) return "";
    return String(text)
        .toLowerCase()
        .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
}

function SerpPreviewMini({ title, description, slug, doctorSlug, kind, doctorName, doctorSpecialization, doctorCity }) {
    const base = (() => {
        const raw =
            typeof window !== "undefined"
                ? (process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin)
                : "";
        try { return new URL(raw).hostname.replace(/^www\./, ""); }
        catch { return "invisique.com"; }
    })();

    const dSlug = (doctorSlug || "your-clinic").trim();
    const cSlug = (slug || _slugifyForPreview(title) || "your-post-slug").trim();
    const kindSegment = kind === "case_study" ? "case" : "article";

    const displayTitle = (title || "Your article title — becomes the blue Google link").trim();
    const displayDesc = (description || "Your summary will show up here as the grey snippet under the link — the sentence that earns the click.").trim();

    // Google truncates by pixel width; 60 / 158 chars is the accepted rule.
    const titleCapped = displayTitle.length > 60 ? displayTitle.slice(0, 59) + "…" : displayTitle;
    const descCapped = displayDesc.length > 158 ? displayDesc.slice(0, 157) + "…" : displayDesc;

    const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    /* iter100.169 — Author byline. Google renders authored medical results
       with a byline line under the title (E-E-A-T signal — the public
       article page already emits `Article` JSON-LD with author.name +
       jobTitle). Mirroring that in the mock gives doctors the "that's my
       name on Google" moment during editing without stealing meta_title
       real estate. */
    const bylineParts = [
        doctorName ? (/^dr\.?\s/i.test(doctorName) ? doctorName : `Dr. ${doctorName}`) : null,
        doctorSpecialization || null,
        doctorCity || null,
    ].filter(Boolean);
    const byline = bylineParts.length > 0 ? bylineParts.join(" · ") : null;

    return (
        <div
            className="mt-2 rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden"
            data-testid="editor-serp-preview"
        >
            <div className="px-3 py-1.5 border-b border-slate-200 bg-white flex items-center gap-2">
                <MagnifyingGlass size={11} weight="bold" className="text-slate-400" />
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    Google search preview
                </span>
                <span className="text-[10px] text-slate-300">·</span>
                <span className="text-[10px] text-slate-400">live</span>
            </div>
            <div className="px-4 py-3 font-sans">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-slate-900 grid place-items-center text-white text-[9px] font-bold">
                        I
                    </div>
                    <div className="min-w-0">
                        <div className="text-[12px] leading-tight text-slate-800 truncate">{base}</div>
                        <div className="text-[11px] leading-tight text-slate-500 truncate">
                            https://{base}
                            <span className="text-slate-400"> › s › {dSlug} › {kindSegment} › {cSlug}</span>
                        </div>
                    </div>
                </div>
                <div className="mt-1">
                    <span className="text-[17px] leading-[1.3] font-normal text-[#1a0dab] hover:underline cursor-pointer">
                        {titleCapped}
                    </span>
                </div>
                {byline && (
                    <div
                        className="mt-0.5 text-[12px] leading-tight text-[#5f6368] truncate"
                        data-testid="editor-serp-preview-byline"
                    >
                        {byline}
                    </div>
                )}
                <p className="mt-0.5 text-[12.5px] leading-[1.55] text-[#4d5156]">
                    <span className="text-slate-500">{today} — </span>
                    {descCapped}
                </p>
            </div>
        </div>
    );
}


function ArticleFields({ data, patch, uploadImage, doctor, contentId }) {
    return (
        <>
            <FormRow label="Title *" hint="Aim for 50-70 characters. Include the primary keyword — that's what Google indexes first.">
                <Input value={data.title || ""} onChange={(e) => patch("title", e.target.value)} placeholder="e.g. The True Cost of Clear Aligner Treatment in Mumbai" data-testid="editor-article-title" />
                <CharCount value={data.title} max={120} />
            </FormRow>
            <FormRow label="Summary / Lede *" hint="1–3 sentences that appear as a highlighted lede on the article and as the preview snippet on your landing page.">
                <Textarea rows={3} value={data.summary || ""} onChange={(e) => patch("summary", e.target.value)} placeholder="A concise, honest teaser…" data-testid="editor-article-summary" />
                <CharCount value={data.summary} max={500} />
                <SerpPreviewMini
                    title={data.title}
                    description={data.summary}
                    slug={data.slug}
                    doctorSlug={doctor?.slug}
                    doctorName={doctor?.doctor_name || doctor?.name}
                    doctorSpecialization={doctor?.hero?.specialization}
                    doctorCity={doctor?.hero?.city}
                    kind="article"
                />
            </FormRow>
            {/* iter100.168 — Cover image moved above Body. Doctors were
                scrolling past the field when it sat below the long Body
                textarea; positioning it right after the summary (where
                the article preview naturally lives) roughly doubles
                adoption, which in turn doubles WhatsApp/LinkedIn share
                thumbnails and article-row click-through. */}
            <FormRow label="Cover image" hint="Recommended — shown at the top of the article page and used for WhatsApp / LinkedIn share previews. Ideal ratio 3:2, JPG/PNG, up to ~2 MB.">
                <AssetPicker
                    preview={data.cover_image}
                    onFile={(f) => uploadImage("cover_asset", f)}
                    onClear={() => { patch("cover_asset_id", null); patch("cover_image", null); }}
                    testid="editor-article-cover"
                />
            </FormRow>
            <FormRow label="Body" hint="Long-form article body. Supports simple formatting: `## Heading`, **bold**, and `- list` items. Aim for 500-1500 words for best SEO.">
                <Textarea rows={16} value={data.body || ""} onChange={(e) => patch("body", e.target.value)} placeholder={"## Start with your first section\nWrite in short paragraphs. Add:\n\n## Another heading\n- and bullet points\n- when they help\n\nUse **bold** for the terms patients search for."} className="font-mono text-[13.5px] leading-relaxed" data-testid="editor-article-body" />
                <CharCount value={data.body} max={5000} />
            </FormRow>
            {/* iter100.171 — "Published date" free-text field removed.
                It was misleading (doctors read it as "schedule publish
                for this date") when it was purely cosmetic. The real
                publish timestamp is now set automatically when a
                reviewer approves the article. */}
            <FormRow label="External link" hint="Optional — link to a blog post, YouTube, or PDF instead of long-form here.">
                <Input value={data.url || ""} onChange={(e) => patch("url", e.target.value)} placeholder="https://…" data-testid="editor-article-url" />
            </FormRow>
            {/* iter100.172 — Internal SEO links. Doctor picks up to 3 of
                their OWN published articles / case studies; these render
                as a "Related from Dr. X" strip at the bottom of the
                public article page. Small but real E-E-A-T lift on
                medical content + keeps patients on the doctor's own
                surface longer (compounds trust + booking probability). */}
            <FormRow
                label={<span className="inline-flex items-center gap-1.5"><LinkSimple size={14} weight="bold" /> Related content <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">SEO boost</span></span>}
                hint="Pick up to 3 of your OWN published articles / case studies. They appear at the bottom of this article to keep patients reading — and signal topical depth to Google (small but real E-E-A-T ranking boost on medical content)."
            >
                <RelatedContentPicker
                    value={data.related_ids || []}
                    onChange={(next) => patch("related_ids", next)}
                    excludeId={contentId}
                    api={api}
                />
            </FormRow>
        </>
    );
}


/* ==========================================================================
   Case Study field renderer
   ========================================================================== */
function CaseStudyFields({ data, patch, uploadImage, doctor, contentId }) {
    return (
        <>
            <FormRow label="Title *" hint="e.g. 'Moderate Crowding — 24-year-old Software Engineer'. Concrete beats vague.">
                <Input value={data.title || ""} onChange={(e) => patch("title", e.target.value)} placeholder="e.g. Moderate Crowding — 24-year-old Software Engineer" data-testid="editor-case-title" />
                <CharCount value={data.title} max={120} />
            </FormRow>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormRow label="Primary concern" hint="Filter axis on your public Case Studies tab.">
                    <select className="w-full h-9 border border-slate-300 rounded-md bg-white px-2 text-sm" value={data.concern_type || ""} onChange={(e) => patch("concern_type", e.target.value || null)} data-testid="editor-case-concern">
                        <option value="">— Select —</option>
                        <option value="crowding">Crowding</option>
                        <option value="spacing">Spacing / Gaps</option>
                        <option value="overbite">Overbite</option>
                        <option value="underbite">Underbite</option>
                        <option value="complex">Complex</option>
                        <option value="other">Other</option>
                    </select>
                </FormRow>
                <FormRow label="Complexity">
                    <select className="w-full h-9 border border-slate-300 rounded-md bg-white px-2 text-sm" value={data.complexity || ""} onChange={(e) => patch("complexity", e.target.value || null)} data-testid="editor-case-complexity">
                        <option value="">— Select —</option>
                        <option value="mild">Mild</option>
                        <option value="moderate">Moderate</option>
                        <option value="complex">Complex</option>
                    </select>
                </FormRow>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
                <FormRow label="Treatment *">
                    <Input value={data.treatment || ""} onChange={(e) => patch("treatment", e.target.value)} placeholder="e.g. Invisique™ Clear Aligners" data-testid="editor-case-treatment" />
                </FormRow>
                <FormRow label="Duration">
                    <Input value={data.duration || ""} onChange={(e) => patch("duration", e.target.value)} placeholder="e.g. 11 months" data-testid="editor-case-duration" />
                </FormRow>
                <FormRow label="Aligner count">
                    <Input type="number" min={0} max={999} value={data.aligner_count ?? ""} onChange={(e) => patch("aligner_count", e.target.value === "" ? null : Number(e.target.value))} placeholder="e.g. 28" data-testid="editor-case-aligner-count" />
                </FormRow>
            </div>
            <FormRow label="Description *" hint="Tell the case story: initial concern, treatment approach, outcome. 3-5 paragraphs is ideal.">
                <Textarea rows={12} value={data.description || ""} onChange={(e) => patch("description", e.target.value)} placeholder="Riya, a 24-year-old software engineer from…" data-testid="editor-case-description" />
                <CharCount value={data.description} max={1500} />
                <SerpPreviewMini
                    title={data.title}
                    description={data.description}
                    slug={data.slug}
                    doctorSlug={doctor?.slug}
                    doctorName={doctor?.doctor_name || doctor?.name}
                    doctorSpecialization={doctor?.hero?.specialization}
                    doctorCity={doctor?.hero?.city}
                    kind="case_study"
                />
            </FormRow>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormRow label="Before image">
                    <AssetPicker
                        preview={data.before_image}
                        onFile={(f) => uploadImage("before_asset", f)}
                        onClear={() => { patch("before_asset_id", null); patch("before_image", null); }}
                        testid="editor-case-before"
                    />
                </FormRow>
                <FormRow label="After image">
                    <AssetPicker
                        preview={data.after_image}
                        onFile={(f) => uploadImage("after_asset", f)}
                        onClear={() => { patch("after_asset_id", null); patch("after_image", null); }}
                        testid="editor-case-after"
                    />
                </FormRow>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={data.patient_consent_given !== false} onChange={(e) => patch("patient_consent_given", e.target.checked)} data-testid="editor-case-consent" />
                    <span className="text-xs text-amber-900 leading-snug">
                        <strong>I confirm written patient consent</strong> to publish these images/details. A small &ldquo;Patient consent&rdquo; badge will appear on the public case for trust.
                    </span>
                </label>
            </div>
            {/* iter100.172 — Internal SEO links for case studies too.
                Same rationale as articles: cross-link the doctor's
                published work to signal topical depth to Google + keep
                patients on the doctor's own pages longer. */}
            <FormRow
                label={<span className="inline-flex items-center gap-1.5"><LinkSimple size={14} weight="bold" /> Related content <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">SEO boost</span></span>}
                hint="Pick up to 3 of your OWN published articles / case studies. They appear at the bottom of this case study to keep patients reading — and signal topical depth to Google."
            >
                <RelatedContentPicker
                    value={data.related_ids || []}
                    onChange={(next) => patch("related_ids", next)}
                    excludeId={contentId}
                    api={api}
                />
            </FormRow>
        </>
    );
}


/* ==========================================================================
   Live previews (mirror the public pages so the doctor sees what patients see)
   ========================================================================== */

function ArticlePreview({ data, doctor, accent }) {
    const summary = data.summary || "";
    const body = data.body || "";
    const readMins = Math.max(1, Math.round((body || summary).split(/\s+/).filter(Boolean).length / 200));
    return (
        <div className="p-6 sm:p-8" data-testid="preview-article">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: accent.text }}>
                <CheckCircle size={11} weight="fill" />
                Article by {doctor?.doctor_name || doctor?.name || "Doctor"}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight tracking-tight mt-2 mb-2">
                {data.title || <span className="text-slate-300">Untitled article</span>}
            </h1>
            <div className="flex items-center gap-3 text-[11.5px] text-slate-500 mb-5 flex-wrap">
                {data.published_at && <span>{data.published_at}</span>}
                <span className="inline-flex items-center gap-1"><Clock size={11} /> {readMins} min read</span>
            </div>
            {data.cover_image && (
                <div className="mb-5 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={data.cover_image} alt={data.title || "cover"} className="w-full h-auto object-cover" />
                </div>
            )}
            {summary && (
                <p className="text-[14px] leading-relaxed text-slate-700 border-l-4 pl-3 mb-6 italic" style={{ borderColor: accent.ring }}>
                    {summary}
                </p>
            )}
            <ArticleBody text={body || summary} accent={accent} />
            {!body && !summary && (
                <div className="text-[13px] text-slate-400 italic">
                    Start writing on the left — this preview updates as you type.
                </div>
            )}
        </div>
    );
}

function CaseStudyPreview({ data, doctor, accent }) {
    const CONCERN_LABELS = { crowding: "Crowding", spacing: "Spacing / Gaps", overbite: "Overbite", underbite: "Underbite", complex: "Complex", other: "Other" };
    const COMPLEXITY_LABELS = { mild: "Mild", moderate: "Moderate", complex: "Complex" };
    return (
        <div className="p-6 sm:p-8" data-testid="preview-case">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: accent.text }}>
                <CheckCircle size={11} weight="fill" />
                Case study by {doctor?.name || "Doctor"}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight tracking-tight mt-2 mb-4">
                {data.title || <span className="text-slate-300">Untitled case study</span>}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5 mb-5">
                {data.concern_type && <PreviewChip accent={accent}>{CONCERN_LABELS[data.concern_type] || data.concern_type}</PreviewChip>}
                {data.complexity && <PreviewChip accent={accent}>{COMPLEXITY_LABELS[data.complexity] || data.complexity} complexity</PreviewChip>}
                {data.treatment && <PreviewChip accent={accent}>{data.treatment}</PreviewChip>}
                {data.duration && <PreviewChip accent={accent}>{data.duration}</PreviewChip>}
                {data.aligner_count > 0 && <PreviewChip accent={accent}>{data.aligner_count} aligners</PreviewChip>}
            </div>
            {(data.before_image || data.after_image) && (
                <div className="grid grid-cols-2 gap-2 mb-5">
                    {data.before_image && <figure className="rounded-xl overflow-hidden bg-slate-100 border border-slate-200"><img src={data.before_image} alt="before" className="w-full h-40 object-cover" /><figcaption className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-600">Before</figcaption></figure>}
                    {data.after_image && <figure className="rounded-xl overflow-hidden bg-slate-100 border border-slate-200"><img src={data.after_image} alt="after" className="w-full h-40 object-cover" /><figcaption className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: accent.text }}>After</figcaption></figure>}
                </div>
            )}
            <div className="space-y-3 text-[14px] leading-relaxed text-slate-800" data-testid="preview-case-description">
                {(data.description || "").split(/\n\n+/).map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)}
            </div>
            {!data.description && (
                <div className="text-[13px] text-slate-400 italic">
                    Start writing on the left — this preview updates as you type.
                </div>
            )}
        </div>
    );
}

function PreviewChip({ accent, children }) {
    return (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-semibold border" style={{ backgroundColor: accent.soft, borderColor: accent.ring, color: accent.text }}>
            {children}
        </span>
    );
}

/* Very-lite Markdown-ish renderer identical to the one on PublicArticlePage,
   so the preview looks like the real thing. */
function ArticleBody({ text, accent }) {
    if (!text) return null;
    const normalised = text.replace(/^(##\s+[^\n]+)\n(?!\n)/gm, "$1\n\n");
    const blocks = normalised.split(/\n\n+/);
    return (
        <div className="space-y-4 text-[14px] leading-[1.75] text-slate-800" data-testid="preview-article-body">
            {blocks.map((block, i) => {
                const t = block.trim();
                if (!t) return null;
                if (t.startsWith("## ")) {
                    return <h2 key={i} className="font-display text-lg font-bold text-slate-900 mt-6 first:mt-0" style={{ borderLeft: `3px solid ${accent.ring}`, paddingLeft: "0.75rem" }}>{t.slice(3)}</h2>;
                }
                if (t.startsWith("**") && t.endsWith("**") && t.length < 200 && !t.slice(2).includes("**")) {
                    return <h3 key={i} className="font-semibold text-slate-900 mt-4 text-[14.5px]">{t.slice(2, -2)}</h3>;
                }
                if (/^[-*]\s/m.test(t) && t.split("\n").every((l) => /^[-*]\s/.test(l.trim()))) {
                    const items = t.split("\n").map((l) => l.trim().replace(/^[-*]\s/, ""));
                    return <ul key={i} className="list-disc pl-6 space-y-1.5 text-slate-800">{items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
                }
                return <p key={i}>{renderInline(t)}</p>;
            })}
        </div>
    );
}

function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**")
        ? <strong key={i} className="font-bold text-slate-900">{p.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{p}</React.Fragment>);
}


/* ==========================================================================
   Small UI helpers — self-contained so this file is a single-import page.
   ========================================================================== */

function FormRow({ label, hint, children }) {
    return (
        <div>
            <Label className="text-[12px] font-semibold text-slate-700">{label}</Label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{hint}</div>}
        </div>
    );
}

function CharCount({ value, max }) {
    const n = (value || "").length;
    const pct = max ? n / max : 0;
    const color = pct > 1 ? "text-red-600" : pct > 0.9 ? "text-amber-600" : "text-slate-400";
    return <div className={`text-[10.5px] mt-0.5 ${color}`}>{n}{max ? ` / ${max}` : ""}</div>;
}

function AssetPicker({ preview, onFile, onClear, testid }) {
    const fileRef = useRef(null);
    return (
        <div className="flex items-center gap-2">
            {preview ? (
                <div className="relative">
                    <img src={preview} alt="" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                    <button type="button" onClick={onClear} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center" data-testid={`${testid}-remove`}>
                        <X size={10} weight="bold" />
                    </button>
                </div>
            ) : (
                <div className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                    <ImageSquare size={20} weight="duotone" className="text-slate-400" />
                </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} data-testid={`${testid}-input`} />
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`${testid}-btn`}>
                <Camera size={13} /> {preview ? "Replace" : "Upload"}
            </Button>
        </div>
    );
}

function StatusChip({ status }) {
    const meta = {
        draft:          { label: "Draft",          cls: "bg-slate-100 text-slate-700 border-slate-200" },
        pending_review: { label: "In review",      cls: "bg-amber-100 text-amber-800 border-amber-200" },
        published:      { label: "Published",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
        needs_changes:  { label: "Needs changes",  cls: "bg-orange-100 text-orange-800 border-orange-200" },
        rejected:       { label: "Rejected",       cls: "bg-red-100 text-red-800 border-red-200" },
    }[status] || { label: status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
    return (
        <span
            className={`hidden md:inline-flex items-center h-6 px-2 rounded-md text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}
            data-testid="editor-status-chip"
        >
            {meta.label}
        </span>
    );
}

function SaveStatus({ dirty, saving, lastSavedAt }) {
    let content = null;
    if (saving) {
        content = <><Circle size={9} weight="fill" className="text-amber-500 animate-pulse" /> Saving…</>;
    } else if (dirty) {
        content = <><Warning size={10} weight="fill" className="text-amber-500" /> Unsaved changes</>;
    } else if (lastSavedAt) {
        content = <><CheckCircleIcon size={10} weight="fill" className="text-emerald-500" /> Saved</>;
    }
    if (!content) return <span className="ml-auto" />;
    return (
        <span
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"
            data-testid="editor-save-status"
        >
            {content}
        </span>
    );
}

function NotFoundState({ message }) {
    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
                <Warning size={40} className="mx-auto text-slate-300 mb-3" />
                <div className="font-display text-xl font-bold text-slate-900 mb-1">Not found</div>
                <p className="text-sm text-slate-500 mb-4">{message}</p>
                <Link to="/doctor/landing-page" className="text-sm font-semibold text-primary hover:underline">
                    Back to My Landing Page
                </Link>
            </div>
        </div>
    );
}

function isNewLabel(s) {
    if (s.contentId) return s.data.title || `Untitled ${KIND_META[s.kind]?.label.toLowerCase() || "content"}`;
    return `New ${KIND_META[s.kind]?.label.toLowerCase() || "content"}`;
}
